import { api, endpoints } from "../api";
import { AxiosPromise } from "axios";
import { BaseApiPaging, TrainerApi } from "api/types";
import { TRAINER_FORM_FIELDS, TrainerFormValues } from "pages/Trainers/TrainerForm/TrainerForm";
import { TrainerGroupFormValues } from "pages/TrainerGroupsPage/TrainerGroupForm/TrainerGroupForm";
import { TRAINER_TITLES } from "consts/trainerTitles";

const fetch = (
  skip: number,
  take: number,
  searchPhrase: string,
  filter: string,
  orderBy?: string,
): AxiosPromise<BaseApiPaging<TrainerApi>> => {
  return api.get<BaseApiPaging<TrainerApi>>(endpoints.TRAINERS.GET(skip, take, searchPhrase, filter, orderBy));
};

const fetchGroups = (
  skip: number,
  take: number,
  searchPhrase: string,
  filter: string,
  orderBy?: string,
): AxiosPromise<BaseApiPaging<TrainerGroupApi>> => {
  return api.get<BaseApiPaging<TrainerGroupApi>>(
    endpoints.TRAINERS.GET_GROUPS(skip, take, searchPhrase, filter, orderBy),
  );
};

const fetchById = (trainerId: number): AxiosPromise<TrainerApi> => {
  return api.get<TrainerApi>(endpoints.TRAINERS.GET_BY_ID(trainerId));
};

const deleteTrainer = (trainerId: number): Promise<void> => {
  return api.delete(endpoints.TRAINERS.DELETE(trainerId));
};

const create = (data: TrainerFormValues): AxiosPromise<TrainerApi> => {
  const { title, groups, prices, ...request } = data;
  const titleLabel = TRAINER_TITLES.find((x) => x.value == data.title)?.label ?? "";

  const formData = new FormData();

  formData.append("title", titleLabel);
  (groups ?? []).forEach((gr) => {
    formData.append("groups[]", gr ?? "");
  });
  (prices ?? []).forEach((price, index) => {
    formData.append(`prices[${index}].${TRAINER_FORM_FIELDS.PRICES_NAME}`, price.name);
    formData.append(`prices[${index}].${TRAINER_FORM_FIELDS.PRICES_AMOUNT}`, price.amount.toString());
    formData.append(`prices[${index}].${TRAINER_FORM_FIELDS.PRICES_TYPE}`, price.type.toString());
  });
  Object.entries(request).map(([key, value]) => {
    //@ts-ignore
    formData.append(key, value ?? "");
    return;
  });

  return api.post<TrainerApi>(endpoints.TRAINERS.CREATE(), formData);
};

const update = (trainerId: number, data: TrainerFormValues): AxiosPromise<TrainerApi> => {
  const avatarNotChanged = typeof data.avatar === "string";
  const avatar = avatarNotChanged ? undefined : data.avatar;
  const { title, groups, prices, ...request } = data;
  const titleLabel = TRAINER_TITLES.find((x) => x.value == data.title)?.label ?? "";
  const formData = new FormData();
  formData.append("title", titleLabel);
  (groups ?? []).forEach((gr) => {
    formData.append("groups[]", gr ?? "");
  });
  (prices ?? []).forEach((price, index) => {
    formData.append(`prices[${index}].${TRAINER_FORM_FIELDS.PRICES_NAME}`, price.name);
    formData.append(`prices[${index}].${TRAINER_FORM_FIELDS.PRICES_AMOUNT}`, price.amount.toString());
    formData.append(`prices[${index}].${TRAINER_FORM_FIELDS.PRICES_TYPE}`, price.type.toString());
  });
  Object.entries({ ...request, avatar }).map(([key, value]) => {
    //@ts-ignore
    formData.append(key, value ?? "");
    return;
  });

  return api.put<TrainerApi>(endpoints.TRAINERS.UPDATE(trainerId), formData);
};

const createGroup = (data: TrainerGroupFormValues): AxiosPromise<number> => {
  return api.post<number>(endpoints.TRAINERS.CREATE_GROUP(), data);
};

const fetchGroupById = (groupId: number): AxiosPromise<TrainerGroupApi> => {
  return api.get<TrainerGroupApi>(endpoints.TRAINERS.GET_GROUP_BY_ID(groupId));
};

const deleteTrainerGroup = (groupId: number): Promise<void> => {
  return api.delete(endpoints.TRAINERS.DELETE_GROUP(groupId));
};

const fetchWorkHistory = (trainerId: number): AxiosPromise<TrainerWorkHistoryApi> => {
  return api.get<TrainerWorkHistoryApi>(endpoints.TRAINERS.GET_WORK_HISTORY(trainerId));
};

const sendTrainerSchedule = (trainerId: number, courseId: number): AxiosPromise => {
  return api.post(endpoints.TRAINERS.SEND_SCHEDULE(trainerId, courseId));
};

export const trainersApi = {
  fetch,
  fetchById,
  delete: deleteTrainer,
  create,
  fetchGroups,
  fetchGroupById,
  deleteTrainerGroup,
  createGroup,
  fetchWorkHistory,
  sendTrainerSchedule,
  update,
};

export interface TrainerGroupApi {
  id: number;
  name: string;
  order: number;
}

export interface TrainerWorkHistoryApi {
  trainerId: number;
  events: TrainerWorkHistoryEventApi[];
}

export interface TrainerWorkHistoryEventApi {
  course: {
    courseId: number;
    courseName: string;
    courseDate: Date;
    courseEndDate: Date;
  };
  event: {
    courseScheduleId: number;
    name: string;
    eventDate: Date;
    timeFrom: string;
    timeTo: string;
  };
}
